import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { translations } from "@/i18n";
import type { Language } from "@/i18n/types";

/**
 * Le dictionnaire vit dans src/i18n, découpé en 26 modules par domaine.
 * Il est réexporté ici parce qu'une dizaine de tests l'importent depuis
 * ce chemin, et parce que c'est ici que `t()` le consomme.
 */
export { translations };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  /** Applique une langue par défaut (ex. langue du salon) UNIQUEMENT si
   *  l'utilisateur n'a pas déjà fait un choix explicite. */
  setLanguageIfNotExplicit: (lang: Language) => void;
  /** Traduit `key` dans la langue active. Si la clé est absente, retourne
   *  `fallback` (le libellé par défaut écrit en ligne) sinon la clé brute. */
  t: (key: string, fallback?: string) => string;
  pluralize: (count: number, singularKey: string, pluralKey: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Safari lève SecurityError dès qu'on touche localStorage quand le stockage du
// site est interdit (cookies bloqués). Ce fournisseur est à la racine : sans ces
// gardes, toute l'app tombe sur l'écran d'erreur (Sentry JAVASCRIPT-REACT-58).
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* stockage interdit : le choix tient pour la session seulement */
  }
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const stored = readStored("app_language");
    if (stored === "en" || stored === "fr") return stored;
    const browserLang = navigator.language?.toLowerCase() ?? "";
    return browserLang.startsWith("en") ? "en" : "fr";
  });

  const handleSetLanguage = useCallback((lang: Language) => {
    setLanguage(lang);
    writeStored("app_language", lang);
    writeStored("app_language_explicit", "true");
  }, []);

  const setLanguageIfNotExplicit = useCallback((lang: Language) => {
    if (readStored("app_language_explicit") === "true") return;
    if (lang !== "en" && lang !== "fr") return;
    setLanguage(lang);
    writeStored("app_language", lang);
  }, []);

  const t = (key: string, fallback?: string): string => {
    // translations[language] is a large literal type; index it as a string map.
    return (translations[language] as Record<string, string>)[key] || fallback || key;
  };

  const pluralize = (count: number, singularKey: string, pluralKey: string): string => {
    // For French: 0 or 1 = singular, 2+ = plural
    // For English: 1 = singular, anything else = plural
    if (language === "fr") {
      return count <= 1 ? t(singularKey) : t(pluralKey);
    } else {
      return count === 1 ? t(singularKey) : t(pluralKey);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, setLanguageIfNotExplicit, t, pluralize }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
